import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, Mic } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import useDebounce from '../../hooks/useDebounce';
import { RootState } from '../../reducers';
import { screenAppAPIClient } from '../../lib/baseAxios';
import { FileType } from '../../types';
import { getFileUrl } from '../../lib/routingUtils';

interface SearchDialogProps {
  open: boolean;
  onClose: () => void;
  teamId?: string;
}

interface SearchResult extends FileType {
  fileId?: string;
  fileName?: string;
  recordingId?: string;
  thumbUrl?: string;
  streams?: ('audio' | 'video')[];
}

const SearchDialog: React.FC<SearchDialogProps> = ({
  open,
  onClose,
  teamId: propsTeamId,
}) => {
  const [files, setFiles] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalFiles, setTotalFiles] = useState(0);
  const [hasMoreFiles, setHasMoreFiles] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(false);
  const [shouldLoadMore, setShouldLoadMore] = useState(false);
  const [preloadedPages, setPreloadedPages] = useState<Set<number>>(new Set());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hadFilesOnOpen, setHadFilesOnOpen] = useState(false);

  const ITEMS_PER_PAGE = 20;
  const debouncedShouldLoadMore = useDebounce(shouldLoadMore, 50);
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const navigate = useNavigate();
  const { teamId: paramsTeamId } = useParams<{ teamId?: string }>();
  const teamState = useSelector((state: RootState) => state.team.selectedTeam);

  // Helper function to render file thumbnail
  const renderThumbnail = (file: SearchResult, fileName: string) => {
    // Audio-only file
    if (file.streams && !file.streams.includes('video')) {
      return (
        <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
          <Mic className="w-4 h-4 text-gray-400 dark:text-gray-600 opacity-50" />
        </div>
      );
    }

    // Video file with thumbnail
    if (file.thumbUrl) {
      return (
        <img
          src={file.thumbUrl}
          alt={fileName}
          className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-gray-800"
        />
      );
    }

    // Video file without thumbnail
    return (
      <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0" />
    );
  };

  // Use teamId from props, params, or teamState
  const teamId = propsTeamId || paramsTeamId || teamState?.teamId;
  console.log('TeamId sources:', {
    propsTeamId,
    paramsTeamId,
    teamStateId: teamState?.teamId,
    finalTeamId: teamId,
  });

  const fetchFiles = useCallback(
    async (page: number, append: boolean = false, search?: string) => {
      if (!teamId) return;

      try {
        if (!append) {
          setLoading(true);
        } else {
          setIsLoadingMore(true);
        }
        setError(null);

        const response = await (screenAppAPIClient as any).api.get(
          `/v2/files`,
          {
            params: {
              teamId,
              searchQuery: search || searchQuery || undefined,
              sortBy: 'createdAt',
              sortOrder: 'desc',
              page,
              limit: ITEMS_PER_PAGE,
            },
          },
        );

        if (response.data.success && response.data.data) {
          const newFiles = response.data.data.fileSystem || [];
          const total = response.data.data.pagination?.total || newFiles.length;

          if (append) {
            setFiles((prev) => [...prev, ...newFiles]);
          } else {
            setFiles(newFiles);
          }

          setTotalFiles(total);
          setCurrentPage(page);
          setHasMoreFiles(
            newFiles.length === ITEMS_PER_PAGE &&
              files.length + newFiles.length < total,
          );
          setPreloadedPages((prev) => new Set([...prev, page]));
        } else {
          setError('Failed to load files');
        }
      } catch (err: any) {
        console.error('Error fetching files:', err);
        setError('Failed to load files');
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [teamId, ITEMS_PER_PAGE, files.length, searchQuery],
  );

  // Initial fetch when modal opens
  useEffect(() => {
    if (!open || !teamId) return;

    // Reset state when modal opens
    setFiles([]);
    setCurrentPage(1);
    setTotalFiles(0);
    setHasMoreFiles(true);
    setPreloadedPages(new Set());
    setSearchQuery('');
    setHadFilesOnOpen(false);

    const initialFetch = async () => {
      await fetchFiles(1, false);
    };
    initialFetch();
  }, [open, teamId]);

  // Track when we first get files
  useEffect(() => {
    if (files.length > 0 && !hadFilesOnOpen) {
      setHadFilesOnOpen(true);
    }
  }, [files.length, hadFilesOnOpen]);

  // Refetch when debounced search query changes
  useEffect(() => {
    if (!open || !teamId) return;

    // Don't trigger on initial render
    if (debouncedSearchQuery === '' && files.length === 0 && !hadFilesOnOpen) {
      return;
    }

    // Reset pagination state when search changes
    setCurrentPage(1);
    setTotalFiles(0);
    setHasMoreFiles(true);
    setPreloadedPages(new Set());

    fetchFiles(1, false, debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  // Scroll handling for infinite loading
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget;
      const { scrollHeight, scrollTop, clientHeight } = target;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

      // Preload when user is 2 viewport heights away from bottom for seamless scrolling
      const preloadThreshold = window.innerHeight * 2;
      // Show skeleton only when very close to bottom (half viewport)
      const skeletonThreshold = window.innerHeight * 0.5;

      // Update near-bottom state for skeleton display
      setIsNearBottom(distanceFromBottom < skeletonThreshold);

      if (
        distanceFromBottom < preloadThreshold &&
        !loading &&
        !isLoadingMore &&
        hasMoreFiles
      ) {
        const nextPage = currentPage + 1;
        const maxPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);

        // Prevent duplicate page requests
        if (nextPage > maxPages || preloadedPages.has(nextPage)) {
          return;
        }

        // Trigger the debounced load
        setShouldLoadMore(true);
      } else {
        setShouldLoadMore(false);
      }
    },
    [
      loading,
      isLoadingMore,
      hasMoreFiles,
      currentPage,
      totalFiles,
      ITEMS_PER_PAGE,
      preloadedPages,
    ],
  );

  // Effect to handle the actual API call when debounced trigger fires
  useEffect(() => {
    if (debouncedShouldLoadMore && !isLoadingMore && hasMoreFiles) {
      const nextPage = currentPage + 1;
      const maxPages = Math.ceil(totalFiles / ITEMS_PER_PAGE);

      if (nextPage <= maxPages && !preloadedPages.has(nextPage)) {
        fetchFiles(nextPage, true);
      }
    }
  }, [
    debouncedShouldLoadMore,
    isLoadingMore,
    hasMoreFiles,
    currentPage,
    totalFiles,
    ITEMS_PER_PAGE,
    preloadedPages,
    fetchFiles,
  ]);

  const handleFileClick = (file: SearchResult) => {
    if (!teamId) {
      console.error('No team selected');
      return;
    }

    const fileId = file.fileId || file._id || file.id || file.recordingId;
    if (!fileId) {
      console.error('Missing fileId');
      return;
    }

    // Close modal before navigating
    onClose();

    const parentId = file.parentId || 'default';
    const fileUrl = getFileUrl({
      teamId,
      parentId,
      fileId,
    });
    navigate(fileUrl);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="h-[480px] max-h-[80vh] overflow-hidden focus:outline-none focus:ring-0 p-0 gap-0 flex flex-col sm:max-w-[600px]">
        {/* Search Header */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <Search className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search recordings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent outline-none text-base"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="overflow-y-auto overflow-x-hidden w-full"
          >
            {(() => {
              if (loading) {
                return (
                  <div className="pt-3">
                    <div className="space-y-2">
                      {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="flex gap-3 px-6 py-2.5">
                          <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <Skeleton className="h-4 w-full max-w-[70%]" />
                            <Skeleton className="h-3 w-full max-w-[40%]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }

              if (error) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-1">
                      <p className="text-sm font-medium text-destructive">
                        {error}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Please try refreshing the page
                      </p>
                    </div>
                  </div>
                );
              }

              if (files.length === 0) {
                return (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500">
                      {searchQuery
                        ? 'No recordings found'
                        : 'No recent recordings'}
                    </p>
                  </div>
                );
              }

              return (
                <div className="pt-3">
                  <div>
                    {files.map((file) => {
                      const fileId = file.fileId || file._id;
                      const fileName = file.fileName || file.name || 'Untitled';

                      return (
                        <button
                          key={fileId}
                          onClick={() => handleFileClick(file)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleFileClick(file);
                            }
                          }}
                          type="button"
                          className="w-full text-left px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center gap-3"
                        >
                          {/* Thumbnail */}
                          {renderThumbnail(file, fileName)}
                          <div className="font-medium text-sm">{fileName}</div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Loading skeleton for pagination */}
                  {isLoadingMore && isNearBottom && hasMoreFiles && (
                    <div className="space-y-2 py-2">
                      {Array.from({ length: 4 }, (_, i) => (
                        <div
                          key={`skeleton-${i}`}
                          className="flex gap-3 px-6 py-2.5"
                        >
                          <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                          <div className="flex-1 space-y-1">
                            <Skeleton className="h-4 w-full max-w-[70%]" />
                            <Skeleton className="h-3 w-full max-w-[40%]" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchDialog;
